import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X, RefreshCw, Users, UserCheck, Zap, TrendingUp, Calendar, Star, MessageSquare, Activity, Layers } from 'lucide-react';
import {
  collection, query, where, getCountFromServer, Timestamp, getDocs, orderBy, limit,
} from 'firebase/firestore';
import { getDbSafe } from '../services/firebaseConfig';
import { useUserData } from './UserDataContext';
import { formatDuration } from './AccountStatsPanel';

interface Metrics {
  dailySignups: number;
  weeklySignups: number;
  totalUsers: number;
  onboardingComplete: number;
  onboardingTotal: number;
  onboardingMedianMs: number | null;
  onboardingSampleN: number;
  discoverSwipes: number;
  matchedUsers: number;
  d7Retained: number;
  d7Cohort: number;
  streakClaimsToday: number;
  eligibleForStreak: number;
  // ── Yap tracker ──────────────────────────────────────────────────────────
  yapsToday: number;
  yapsWeek: number;
  yapsPriorWeek: number;
  yapsTotal: number;
  yaps30d: number;
  forumPosts30d: number;
  tribePosts30d: number;
  shouts30d: number;
  loading: boolean;
  lastRefresh: Date | null;
  error: string | null;
}

const BLANK: Metrics = {
  dailySignups: 0, weeklySignups: 0, totalUsers: 0,
  onboardingComplete: 0, onboardingTotal: 0,
  onboardingMedianMs: null, onboardingSampleN: 0,
  discoverSwipes: 0, matchedUsers: 0,
  d7Retained: 0, d7Cohort: 0,
  streakClaimsToday: 0, eligibleForStreak: 0,
  yapsToday: 0, yapsWeek: 0, yapsPriorWeek: 0, yapsTotal: 0,
  yaps30d: 0, forumPosts30d: 0, tribePosts30d: 0, shouts30d: 0,
  loading: true, lastRefresh: null, error: null,
};

// A metric that failed to load is stored as -1 (see settle() in fetch) so a
// broken query shows as "—" next to the metrics that did load, instead of
// silently reading as a real zero.
const FAILED = -1;
const num = (v: number) => (v === FAILED ? '—' : v.toLocaleString());

function pct(numerator: number, denom: number) {
  if (numerator === FAILED || denom === FAILED) return '—';
  return denom > 0 ? `${Math.round((numerator / denom) * 100)}%` : '—';
}

// Period-over-period change, signed. Returns "—" when either side failed, and
// "New" when the prior period was empty (a percentage change from zero is
// undefined, and rendering "+Infinity%" or "+100%" would both be misleading).
function delta(current: number, prior: number) {
  if (current === FAILED || prior === FAILED) return '—';
  if (prior === 0) return current > 0 ? 'New' : '0%';
  const change = Math.round(((current - prior) / prior) * 100);
  return `${change >= 0 ? '+' : ''}${change}%`;
}

export function GrowthDashboard({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { userProfile } = useUserData();
  const [m, setM] = useState<Metrics>(BLANK);

  const isAdmin = userProfile?.rank === 'legion' || userProfile?.rank === 'watcher';

  const fetch = useCallback(async () => {
    setM(prev => ({ ...prev, loading: true, error: null }));
    try {
      const db = await getDbSafe();
      const now = new Date();

      const ts = (d: Date) => Timestamp.fromDate(d);
      const daysAgo = (n: number) => { const d = new Date(now); d.setDate(d.getDate() - n); return d; };
      const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);

      const users  = collection(db, 'users');
      const notifs = collection(db, 'notifications');

      // allSettled, not all: these are ten independent aggregate queries and each
      // needs its own composite index. Under Promise.all a single missing index
      // rejected the batch and blanked the whole dashboard — now a metric that
      // can't be counted reports -1 (rendered as "—") and the rest still show.
      const settle = async (results: PromiseSettledResult<any>[]) => results.map((r) => {
        if (r.status === 'fulfilled') return r.value.data().count as number;
        console.warn('[GrowthDashboard] metric failed:', (r.reason as any)?.message || r.reason);
        return -1;
      });

      const [
        daily, weekly, total,
        ob30, obDone,
        swipes,
        matched30,
        d7Cohort,
        streakToday, eligible,
      ] = await settle(await Promise.allSettled([
        getCountFromServer(query(users,  where('createdAt', '>=', ts(todayStart)))),
        getCountFromServer(query(users,  where('createdAt', '>=', ts(daysAgo(7))))),
        getCountFromServer(query(users)),
        getCountFromServer(query(users,  where('createdAt', '>=', ts(daysAgo(30))))),
        // Onboarding completion keys off setupComplete (written by
        // ProfileSetupScreen when a user finishes setup). The previous query used
        // isPlaceholderAccount == false, which never matches: that flag is only
        // ever written as `true` for temp accounts and is absent otherwise, and a
        // missing field doesn't match an equality filter — so this tile always
        // read 0%.
        getCountFromServer(query(users,  where('createdAt', '>=', ts(daysAgo(30))),
                                        where('setupComplete', '==', true))),
        getCountFromServer(query(notifs, where('type', 'in', ['FRIEND_REQUEST', 'CONNECTION_REQUEST']),
                                        where('timestamp', '>=', ts(todayStart)))),
        getCountFromServer(query(users,  where('createdAt', '>=', ts(daysAgo(30))),
                                        where('friendsCount', '>', 0))),
        getCountFromServer(query(users,  where('createdAt', '>=', ts(daysAgo(14))),
                                        where('createdAt', '<',  ts(daysAgo(7))))),
        getCountFromServer(query(users,  where('goldLastClaimAt', '>=', ts(todayStart)))),
        getCountFromServer(query(users,  where('createdAt', '<', ts(daysAgo(2))))),
      ]));

      // ── Yap tracker ────────────────────────────────────────────────────────
      // Deliberately a SEPARATE allSettled batch from the growth metrics above:
      // both are destructured positionally, so folding these in would mean any
      // future edit to one list could silently shift the other's values.
      //
      // All of these are single-field ranges/equalities on `timestamp`, so they
      // need no composite indexes.
      //
      // Note there is no "share of users who post" tile. The obvious source for
      // it, users.yapsCount, is declared on the User type but never written —
      // every one of the 947 user documents has it absent or 0, so such a tile
      // would read a confident 0% forever. Counting distinct authors instead
      // needs a full scan of the yaps collection, which is not worth doing on
      // every dashboard open.
      const yapsCol   = collection(db, 'yaps');
      const forumCol  = collection(db, 'forum_posts');
      const tribeCol  = collection(db, 'tribe_posts');
      const shoutsCol = collection(db, 'shouts');

      const [
        yapsToday, yapsWeek, yapsPriorWeek, yapsTotal,
        yaps30d, forumPosts30d, tribePosts30d, shouts30d,
      ] = await settle(await Promise.allSettled([
        getCountFromServer(query(yapsCol, where('timestamp', '>=', ts(todayStart)))),
        getCountFromServer(query(yapsCol, where('timestamp', '>=', ts(daysAgo(7))))),
        // The comparison window for the trend tile: 7–14 days ago.
        getCountFromServer(query(yapsCol, where('timestamp', '>=', ts(daysAgo(14))),
                                          where('timestamp', '<',  ts(daysAgo(7))))),
        getCountFromServer(query(yapsCol)),
        getCountFromServer(query(yapsCol,   where('timestamp', '>=', ts(daysAgo(30))))),
        getCountFromServer(query(forumCol,  where('timestamp', '>=', ts(daysAgo(30))))),
        getCountFromServer(query(tribeCol,  where('timestamp', '>=', ts(daysAgo(30))))),
        getCountFromServer(query(shoutsCol, where('timestamp', '>=', ts(daysAgo(30))))),
      ]));

      // D7 retention: from the 7–14d cohort, how many have ≥1 friend
      const cohortN = d7Cohort;
      let d7Ret = -1;
      if (cohortN > 0) {
        try {
          const snap = await getCountFromServer(
            query(users,
              where('createdAt', '>=', ts(daysAgo(14))),
              where('createdAt', '<',  ts(daysAgo(7))),
              where('friendsCount', '>', 0),
            )
          );
          d7Ret = snap.data().count;
        } catch (e: any) {
          console.warn('[GrowthDashboard] d7 retention failed:', e?.message || e);
        }
      } else if (cohortN === 0) {
        d7Ret = 0;
      }

      // The median needs the actual durations, so this is a real document read
      // rather than an aggregation — capped at 500 recent completions, which is
      // plenty for a median and bounded regardless of how the app grows.
      let medianMs: number | null = null;
      let sampleN = 0;
      try {
        const durSnap = await getDocs(query(
          users,
          where('onboardingDurationMs', '>', 0),
          orderBy('onboardingDurationMs'),
          limit(500),
        ));
        const durs = durSnap.docs
          .map((d: any) => Number((d.data() as any).onboardingDurationMs))
          .filter(n => Number.isFinite(n) && n > 0)
          .sort((a2, b2) => a2 - b2);
        sampleN = durs.length;
        if (sampleN > 0) {
          const mid = Math.floor(sampleN / 2);
          medianMs = sampleN % 2 === 0 ? Math.round((durs[mid - 1] + durs[mid]) / 2) : durs[mid];
        }
      } catch {
        // Needs a users(onboardingDurationMs) index, and reads nothing until
        // accounts start completing signup with the field written. Absent is
        // shown as an em-less dash rather than a wrong number.
        medianMs = null;
      }

      setM({
        dailySignups:      daily,
        weeklySignups:     weekly,
        totalUsers:        total,
        onboardingTotal:   ob30,
        onboardingComplete:obDone,
        onboardingMedianMs: medianMs,
        onboardingSampleN:  sampleN,
        discoverSwipes:    swipes,
        matchedUsers:      matched30,
        d7Cohort:          cohortN,
        d7Retained:        d7Ret,
        streakClaimsToday: streakToday,
        eligibleForStreak: eligible,
        yapsToday, yapsWeek, yapsPriorWeek, yapsTotal,
        yaps30d, forumPosts30d, tribePosts30d, shouts30d,
        loading: false,
        lastRefresh: new Date(),
        error: null,
      });
    } catch (err: any) {
      console.error('[GrowthDashboard]', err);
      setM(prev => ({ ...prev, loading: false, error: err?.message || 'Query failed' }));
    }
  }, []);

  useEffect(() => {
    if (isOpen && isAdmin) fetch();
  }, [isOpen, isAdmin, fetch]);

  if (!isOpen || !isAdmin) return null;

  const tiles = [
    {
      label: 'New signups today',
      value: m.loading ? '…' : num(m.dailySignups),
      sub: `${num(m.weeklySignups)} this week · ${num(m.totalUsers)} total`,
      icon: <Users className="w-4 h-4" />,
      accent: '#4478ea',
    },
    {
      label: 'Onboarding completion (30d)',
      value: m.loading ? '…' : pct(m.onboardingComplete, m.onboardingTotal),
      sub: `${num(m.onboardingComplete)} of ${num(m.onboardingTotal)} signups finished profile setup`,
      icon: <UserCheck className="w-4 h-4" />,
      accent: '#F39C12',
    },
    {
      label: 'Typical onboarding time (30d)',
      value: m.loading
        ? '…'
        : (m.onboardingMedianMs === null ? '—' : formatDuration(m.onboardingMedianMs)),
      // MEDIAN, not mean. Duration is measured from account creation, so anyone
      // who abandoned signup and came back a day later contributes a huge value;
      // a handful of those drag an average into uselessness while leaving the
      // median where the typical experience actually is.
      sub: m.onboardingSampleN > 0
        ? `median of ${num(m.onboardingSampleN)} completed signups`
        : 'no completed signups recorded yet',
      icon: <UserCheck className="w-4 h-4" />,
      accent: '#0F9D58',
    },
    {
      label: 'Discover swipe volume today',
      value: m.loading ? '…' : num(m.discoverSwipes),
      sub: 'Right-swipes only (each = a connection request sent)',
      icon: <Zap className="w-4 h-4" />,
      accent: '#E74C3C',
    },
    {
      label: 'Match rate (30d cohort)',
      value: m.loading ? '…' : pct(m.matchedUsers, m.onboardingTotal),
      sub: `${num(m.matchedUsers)} of ${num(m.onboardingTotal)} new users made ≥1 connection`,
      icon: <TrendingUp className="w-4 h-4" />,
      accent: '#2d6a30',
    },
    {
      label: 'Day-7 retention estimate',
      value: m.loading ? '…' : pct(m.d7Retained, m.d7Cohort),
      sub: `${num(m.d7Retained)} of ${num(m.d7Cohort)} users (joined 7–14d ago) have ≥1 connection`,
      icon: <Calendar className="w-4 h-4" />,
      accent: '#5a2d82',
    },
    {
      label: 'Streak claims today',
      value: m.loading ? '…' : num(m.streakClaimsToday),
      sub: `${pct(m.streakClaimsToday, m.eligibleForStreak)} of eligible users claimed daily gold`,
      icon: <Star className="w-4 h-4" />,
      accent: '#b8960a',
    },
    // ── Yap tracker ──────────────────────────────────────────────────────────
    {
      label: 'Yaps posted today',
      value: m.loading ? '…' : num(m.yapsToday),
      sub: `${num(m.yapsWeek)} this week · ${num(m.yapsTotal)} all time`,
      icon: <MessageSquare className="w-4 h-4" />,
      accent: '#2f9e8f',
    },
    {
      label: 'Yap volume, week over week',
      value: m.loading ? '…' : delta(m.yapsWeek, m.yapsPriorWeek),
      sub: `${num(m.yapsWeek)} in the last 7d vs ${num(m.yapsPriorWeek)} the 7d before`,
      icon: <Activity className="w-4 h-4" />,
      accent: '#c2571a',
    },
    {
      label: 'Posts by surface (30d)',
      value: m.loading ? '…' : num(
        [m.yaps30d, m.forumPosts30d, m.tribePosts30d, m.shouts30d].some(v => v === FAILED)
          ? FAILED
          : m.yaps30d + m.forumPosts30d + m.tribePosts30d + m.shouts30d
      ),
      sub: `${num(m.yaps30d)} world · ${num(m.forumPosts30d)} forum · ${num(m.tribePosts30d)} tribe · ${num(m.shouts30d)} shouts`,
      icon: <Layers className="w-4 h-4" />,
      accent: '#7a4fb5',
    },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[10000002] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.94 }}
        className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0D0D0D] shadow-2xl overflow-hidden"
        style={{ maxHeight: '88vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white tracking-tight">Growth Dashboard</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {m.lastRefresh ? `Updated ${m.lastRefresh.toLocaleTimeString()}` : 'Loading…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetch}
              disabled={m.loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${m.loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error */}
        {m.error && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
            {m.error} — check Firestore indexes (notifications[type+timestamp], users[friendsCount], users[goldLastClaimAt])
          </div>
        )}

        {/* Metric tiles */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {tiles.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="p-4 rounded-xl border border-white/8 bg-white/[0.03]"
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="p-1.5 rounded-lg"
                  style={{ background: t.accent + '22', color: t.accent }}
                >
                  {t.icon}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                  {t.label}
                </span>
              </div>
              <p className="text-3xl font-black text-white mb-1 tabular-nums">{t.value}</p>
              <p className="text-[11px] text-zinc-600 leading-snug">{t.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Methodology note */}
        <p className="px-6 pb-5 text-[10px] text-zinc-700 leading-relaxed">
          Swipe volume = friend request notifications sent today (right-swipes only; left-swipes are not logged).
          D7 retention uses friendsCount &gt; 0 as a proxy for engagement. Onboarding completion = setupComplete === true. A metric showing — failed to load.
          All counts are live Firestore reads — no analytics SDK required.
          {' '}Yap counts include soft-deleted and moderator-hidden posts, so they measure what was written rather than what is currently visible.
          &ldquo;World&rdquo; is the yaps collection (world feed plus forum-tagged cross-posts); forum and tribe counts are the group-exclusive collections.
        </p>
      </motion.div>
    </div>,
    document.body
  );
}
